import { Component, Input, computed, effect, signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { SfoUiJSONSchema7 } from '../../metadata.model';
import { RenderMode } from '../types/view.types';
import { DynamicFieldModule } from '../form-fields';
import { SchemaKeywords, SfoUiWidgetType } from '../types';

/** One `_ui_group` worth of controls, in schema order. */
export interface SimpleViewSection {
  /** The `_ui_group` value, or `''` for the ungrouped leading section. */
  name: string;
  controls: string[];
  /**
   * Creator-role controls held back because they are empty — offered behind a
   * single "Add contributor role" menu instead of rendering four empty
   * headings. Only ever non-empty on the section that owns them.
   */
  hiddenRoles: string[];
  /**
   * `_ui_secondary` controls held back because they were empty in the form's
   * initial patch — offered behind a "More fields" menu instead of rendering
   * an empty box. Only ever non-empty on the section that owns them.
   */
  hiddenFields: string[];
}

/**
 * Whether a value counts as EMPTY for the `_ui_secondary` collapse: absent,
 * `null`, `''`, an empty array, or a date-variable object with no non-empty
 * `date-parts`, `raw`, `literal`, `season`, or `circa` — all five are real
 * CSL date-variable sub-fields, and a season-only date ("Spring 2023") is
 * entered data that must not be collapsed away. A date-variable object is
 * the only object shape a `gridLayout` (CSL) schema puts on a
 * `_ui_secondary` field, so this recognises "is a plain object" as "is a
 * date-variable object" rather than walking the schema to confirm it.
 */
function isEmptyPatchValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    const dateValue = value as Record<string, unknown>;
    const dateParts = dateValue['date-parts'];
    const hasDateParts =
      Array.isArray(dateParts) && dateParts.some((entry) => Array.isArray(entry) && entry.length > 0);
    return (
      !hasDateParts &&
      isEmptyPatchValue(dateValue['raw']) &&
      isEmptyPatchValue(dateValue['literal']) &&
      isEmptyPatchValue(dateValue['season']) &&
      isEmptyPatchValue(dateValue['circa'])
    );
  }
  return false;
}

@Component({
  selector: 'sfo-simple-view',
  templateUrl: './simple-view.component.html',
  styleUrls: ['./simple-view.component.scss'],
  imports: [CommonModule, DynamicFieldModule, MatButtonModule, MatIconModule, MatMenuModule],
})
export class SimpleViewComponent {
  @Input() set schema(value: SfoUiJSONSchema7 | null) {
    this.schema$.set(value);
  }
  get schema(): SfoUiJSONSchema7 | null {
    return this.schema$();
  }

  @Input() set aFormControl(value: FormGroup) {
    this.form$.set(value);
  }
  get aFormControl(): FormGroup {
    return this.form$();
  }

  /**
   * The same (transformed) patch object `dynamic-form` just applied to the
   * form — a pure pass-through, set by the host so `_ui_secondary` emptiness
   * can be judged on the PATCH rather than sampled from live form state.
   * Sampling live state would race `dynamic-form`'s `patchValue(patch,
   * { emitEvent: false })`, which never fires a `valueChanges` this
   * component could observe.
   */
  @Input() set patchForm(value: object | null) {
    this.patchForm$.set(value ?? null);
  }
  get patchForm(): object | null {
    return this.patchForm$();
  }

  @Input() renderMode: RenderMode | undefined;

  /**
   * Opt-in field layout: honours each property's `_ui_class` width in a
   * wrapping row, groups by `_ui_group`, and collapses empty creator-role
   * arrays behind one menu.
   *
   * Off by default so every existing `renderMode="simple"` surface (the
   * metadata quick-edit dialog, the settings drawer body) keeps the flat
   * single-column list it has today. Turned on by the CSL reference form,
   * whose schemas are the ones carrying the width and group tags.
   */
  @Input() gridLayout = false;

  private readonly schema$ = signal<SfoUiJSONSchema7 | null>(null);
  private readonly form$ = signal<FormGroup>(new FormGroup({}));
  private readonly patchForm$ = signal<object | null>(null);
  /** Creator roles the user asked for from the "Add contributor role" menu. */
  private readonly revealedRoles = signal<ReadonlySet<string>>(new Set());

  /**
   * Controls found EMPTY in the first non-null patch received for the
   * current schema — captured once and never resampled, so a field the
   * author clears while editing does not fall back out of view (monotonic
   * reveal). Recomputed only when the schema identity changes.
   */
  private readonly initiallyEmptyFields = signal<ReadonlySet<string>>(new Set());
  /** `_ui_secondary` fields the user asked for from the "More fields" menu. */
  private readonly revealedFields = signal<ReadonlySet<string>>(new Set());
  private emptinessSchema: SfoUiJSONSchema7 | null | undefined = undefined;
  private emptinessCaptured = false;

  constructor() {
    // Mirrors the reset lifecycle the schema/type-switch selector already
    // uses: a schema identity change starts a fresh capture window (and
    // drops prior reveals, which may not even apply to the new schema); the
    // first non-null patch seen inside that window fixes the empty set.
    effect(() => {
      const schema = this.schema$();
      const patch = this.patchForm$();

      if (schema !== this.emptinessSchema) {
        this.emptinessSchema = schema;
        this.emptinessCaptured = false;
        this.revealedFields.set(new Set());
        this.initiallyEmptyFields.set(new Set());
      }

      if (!this.emptinessCaptured && patch != null) {
        this.emptinessCaptured = true;
        const empty = new Set<string>();
        for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
          if (isEmptyPatchValue(value)) empty.add(key);
        }
        // Schema properties absent from the patch altogether are empty too.
        for (const key of Object.keys(schema?.properties ?? {})) {
          if (!(key in (patch as Record<string, unknown>))) empty.add(key);
        }
        this.initiallyEmptyFields.set(empty);
      }
    });
  }

  /** Visible top-level controls, in schema order. */
  readonly controlsInOrder = computed(() => {
    const form = this.form$();
    const schema = this.schema$();
    return Object.keys(form?.controls ?? {}).filter(
      (key) => !(schema?.properties?.[key] as SfoUiJSONSchema7)?.[SchemaKeywords.UiHide],
    );
  });

  /**
   * Controls split into `_ui_group` sections, ordered by first appearance so
   * the schema — not this component — decides the reading order. A schema with
   * no groups yields exactly one unnamed section, i.e. the flat list.
   */
  readonly sections = computed<SimpleViewSection[]>(() => {
    const order: string[] = [];
    const byName = new Map<string, SimpleViewSection>();
    const section = (name: string): SimpleViewSection => {
      let found = byName.get(name);
      if (!found) {
        found = { name, controls: [], hiddenRoles: [], hiddenFields: [] };
        byName.set(name, found);
        order.push(name);
      }
      return found;
    };

    const initiallyEmpty = this.initiallyEmptyFields();
    const revealed = this.revealedFields();

    let seenCreator = false;
    for (const control of this.controlsInOrder()) {
      const property = this.propertyFor(control);
      const target = section((property?.[SchemaKeywords.UiGroup] as string) ?? '');

      if (this.isCreatorRole(property)) {
        // Keep the first role (author) as the obvious place to start, plus any
        // role that already holds names or was explicitly asked for.
        const keep = !seenCreator || this.hasEntries(control) || this.revealedRoles().has(control);
        seenCreator = true;
        (keep ? target.controls : target.hiddenRoles).push(control);
        continue;
      }

      // A `_ui_secondary` field only collapses while it was empty in the
      // initial patch AND stays unrevealed — a filled field, and a revealed
      // field that the author later empties, always renders (`gridLayout`
      // only; `SimpleViewComponent.gridLayout` off ignores this entirely
      // since the flat template never reads `sections()`).
      const isSecondary = property?.[SchemaKeywords.UiSecondary] === true;
      if (isSecondary && initiallyEmpty.has(control) && !revealed.has(control)) {
        target.hiddenFields.push(control);
        continue;
      }

      target.controls.push(control);
    }

    return order
      .map((name) => byName.get(name)!)
      .filter((s) => s.controls.length || s.hiddenRoles.length || s.hiddenFields.length);
  });

  /** The width class the cell takes, from the property's own `_ui_class`. */
  cellClass(control: string): string {
    return (this.propertyFor(control)?.[SchemaKeywords.UiClass] as string) ?? 'col-12';
  }

  /**
   * The property schema handed down to the field, with its own `_ui_class`
   * replaced by a plain full-width one. Two reasons: the width now lives on the
   * grid cell, and leaving it on would apply the fraction twice (a third of a
   * third); and the default the field would otherwise fall back to carries the
   * vertical gutter, which the cell owns here so the row spacing is set in one
   * place rather than per field.
   */
  fieldSchema(control: string): SfoUiJSONSchema7 | undefined {
    const property = this.propertyFor(control);
    if (!property) return property;
    let rewritten = this.fieldSchemaCache.get(control);
    if (!rewritten || rewritten.source !== property) {
      const value = { ...property, [SchemaKeywords.UiClass]: 'col-12' } as SfoUiJSONSchema7;
      rewritten = { source: property, value };
      this.fieldSchemaCache.set(control, rewritten);
    }
    return rewritten.value;
  }

  /** Human label for a held-back creator role. */
  roleLabel(control: string): string {
    return (this.propertyFor(control)?.title as string) ?? control;
  }

  /** "Add contributor role" pick — render that role's array from now on. */
  revealRole(control: string): void {
    this.revealedRoles.update((current) => new Set(current).add(control));
  }

  /** Human label for a held-back `_ui_secondary` field. */
  fieldLabel(control: string): string {
    return (this.propertyFor(control)?.title as string) ?? control;
  }

  /**
   * "More fields" pick — render that field from now on, permanently. Reveals
   * are monotonic: emptying the field afterwards must not hide it again.
   */
  revealField(control: string): void {
    this.revealedFields.update((current) => new Set(current).add(control));
  }

  isObject(control: string): boolean {
    return this.propertyFor(control)?.type === 'object';
  }

  getFormControl(control: string): FormGroup {
    return this.form$().get(control) as FormGroup;
  }

  private readonly fieldSchemaCache = new Map<
    string,
    { source: SfoUiJSONSchema7; value: SfoUiJSONSchema7 }
  >();

  private propertyFor(control: string): SfoUiJSONSchema7 | undefined {
    return this.schema$()?.properties?.[control] as SfoUiJSONSchema7 | undefined;
  }

  private isCreatorRole(property: SfoUiJSONSchema7 | undefined): boolean {
    return property?.[SchemaKeywords.UiWidget] === SfoUiWidgetType.CreatorRoleArray;
  }

  private hasEntries(control: string): boolean {
    const array = this.form$().get(control);
    return array instanceof FormArray ? array.length > 0 : false;
  }
}
